import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  FileDto,
  PropertyDocumentDto,
  PropertyDocumentStatus,
  PropertyDocumentType,
  PropertyDto,
  PropertyId,
} from '@workspace/data-types';
import { EtxeEnumsNiceLabelUtils } from '../../../../utils/etxe-enums-nice-label.utils';
import { PropertyDocumentAdminStatusMenuComponent } from '../admin-status-menu/property-document-admin-status-menu.component';

@Component({
  selector: 'etxe-property-document-item',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    PropertyDocumentAdminStatusMenuComponent,
  ],
  templateUrl: './property-document-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDocumentItemComponent {
  doc = input.required<PropertyDocumentDto>();
  propertyId = input.required<PropertyId>();
  editable = input<boolean>(true);
  isAdmin = input<boolean>(false);
  isDeleting = input<boolean>(false);
  canReplace = input<boolean>(false);
  isHistory = input<boolean>(false);

  propertyUpdated = output<PropertyDto>();
  replace = output<void>();
  delete = output<PropertyDocumentDto>();

  PropertyDocumentStatus = PropertyDocumentStatus;
  PropertyDocumentType = PropertyDocumentType;

  isVerified = computed(() => this.doc().status === PropertyDocumentStatus.VERIFIED);
  isRejected = computed(() => this.doc().status === PropertyDocumentStatus.REJECTED);
  isOwnership = computed(() => this.doc().type === PropertyDocumentType.OWNERSHIP);
  canDelete = computed(() => !this.isVerified() && !this.isRejected() && !this.isHistory());

  get fileName(): string {
    const file = this.doc().file;
    if (!file) return 'Document';
    if (typeof file === 'object' && 'filename' in file) {
      return (file as FileDto).filename || 'Document';
    }
    return this.doc().label || 'Document';
  }

  get fileUrl(): string | null {
    const file = this.doc().file;
    if (!file) return null;
    if (typeof file === 'object' && 'url' in file) {
      return (file as FileDto).url || null;
    }
    return this.doc().url || null;
  }

  get fileSize(): number {
    const file = this.doc().file;
    if (!file || typeof file !== 'object' || !('size' in file)) return 0;
    return (file as FileDto).size || 0;
  }

  get formattedFileSize(): string {
    const bytes = this.fileSize;
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} Ko`;
    return `${(kb / 1024).toFixed(1)} Mo`;
  }

  get typeLabel(): string {
    return EtxeEnumsNiceLabelUtils.documentType(this.doc().type);
  }

  get typeIcon(): string {
    switch (this.doc().type) {
      case PropertyDocumentType.OWNERSHIP:
        return 'shield-check';
      case PropertyDocumentType.DPE:
        return 'gauge';
      case PropertyDocumentType.CO_OWNERSHIP:
        return 'building';
      case PropertyDocumentType.FLOOR_PLAN:
        return 'map';
      case PropertyDocumentType.OTHER:
      default:
        return 'file-text';
    }
  }
}
